import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { useState } from 'react';
import { isEmail } from 'lib/is-email';
import { EmailTagInput } from 'components/EmailTagInput';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Loader2 } from 'lucide-react';
import { useMutation } from '@apollo/client';
import { EXPORT } from 'app/graphql';
import { ExportColumns } from 'app/graphql/generated/admin/graphql';
import { toast } from 'sonner';
import { billingItemsColumns } from '../common/ColumnDropdown/utils';
import { ColumnsDropdown } from '../common/ColumnDropdown';
import { InvoiceItem } from './column';

type Props = {
  isOpen: boolean;
  invoiceItem: InvoiceItem;
  onOpenChange: (isOpen: boolean) => void;
};

export const ExportInvoices = ({ isOpen, onOpenChange, invoiceItem }: Props) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<ExportColumns[]>(
    Object.keys(billingItemsColumns) as ExportColumns[],
  );

  const handleSetSelectedColumns = (columns: ExportColumns[]) => {
    setSelectedColumns(columns);
  };

  const [exportBillingItems] = useMutation(EXPORT, {
    context: { clientName: 'admin' },
  });

  const handleExportSubmit = async () => {
    if (selectedColumns.length === 0) {
      toast.error('Please select at least one column to export', {
        id: 'export-billing-period-failed',
      });
    }
    setIsSubmitting(true);
    try {
      await exportBillingItems({
        variables: {
          input: {
            columns: selectedColumns,
            startDate: invoiceItem.startDate,
            endDate: invoiceItem.endDate,
            additionalRecipients: emails,
          },
        },
      });
      onOpenChange(false);
      setEmails([]);
      setSelectedColumns(Object.keys(billingItemsColumns) as ExportColumns[]);
      toast.success('Request to export invoice successful', {
        id: 'export-invoice-success',
      });
    } catch (error) {
      console.error(error);
      toast.error('Error requesting export for invoice', {
        id: 'export-invoice-failed',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Modal Dialog uses the shared backdrop from components/ui/dialog
  // (translucent ink + soft blur), matching every other dialog in the app.
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export report for invoice #{invoiceItem.id}</DialogTitle>
          <DialogDescription>
            An email with a download link will be sent to the email on this account.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label>Columns</Label>
          <ColumnsDropdown
            onColumnChange={handleSetSelectedColumns}
            selectedColumns={selectedColumns}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label>Additional recipients</Label>
          <EmailTagInput
            emails={emails}
            setEmails={setEmails}
            disabled={isSubmitting}
            readOnly={isSubmitting}
            addTagsOnBlur={true}
            inputProps={{ inputMode: 'email' }}
            placeholder="Add email address"
            styleClasses={{
              input: 'w-full sm:max-w-[350px] shadow-none',
              tag: {
                body: 'bg-[var(--surface-2)] text-[var(--text)] border border-[var(--line)] hover:bg-[var(--surface-2)]',
              },
              tagList: { container: 'flex-wrap' },
            }}
            variant="primary"
            validateTag={(tag: string) => {
              return isEmail(tag);
            }}
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            disabled={isSubmitting || selectedColumns.length === 0}
            onClick={handleExportSubmit}
            aria-label="Export invoice data"
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Export'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
